/**
 * Konfiguracja widgetów listy — pulpit KPI nad listą (zakładka „Widgety"
 * w Kreatorze Listy). Port z aplikacji SellCall
 * (apps/admin/src/components/module-list/ListViewSettings.tsx — `WidgetConfig`).
 *
 * Trzymane jako Zod schema (jak `list-view.ts`) żeby backend (endpoint `/stats`
 * liczący wartości) i frontend (edytor + pasek widgetów) miały to samo źródło
 * prawdy o shape'ie, a typy TS pochodziły z `.infer<>`.
 *
 * Wpięte do `ListConfig.widgets` (kolumna `list_config` JSONB). Osobny plik ze
 * względu na rozmiar (pełny parytet SellCall = 14 typów widgetów + ustawienia
 * per typ). Reużywa `filterConfigSchema`/`filterOperatorSchema` z `list-view.ts`
 * (nie duplikuje operatorów ani kształtu filtra).
 *
 * Zakres iteracji (cel: pełne 14 typów):
 *   - I1 (pierwszy mergowalny): `counter` + `aggregate` — karty liczbowe.
 *   - I2: wykresy SVG (`pie`/`bar`/`donut`).
 *   - I3: `trend`/`sparkline`/`gauge`.
 *   - I4: reszta (`heatmap`/`funnel`/`table`/`stat_card`/`progress`/`kpi`)
 *         + conditional formatting + comparison + moduły custom.
 * Schema od razu pełna — edytor odsłania typy iteracyjnie.
 */

import { z } from 'zod';

import { filterConfigSchema, widgetStyleSchema } from './list-view';

/**
 * Leniwa referencja do `filterConfigSchema` z `list-view.ts`. `list-view.ts`
 * importuje stąd `widgetsConfigSchema` (pole `ListConfig.widgets`), więc bez
 * `z.lazy` mielibyśmy cykliczny import rozwiązywany w złej kolejności
 * (filterConfigSchema === undefined w momencie ewaluacji tego modułu). `z.lazy`
 * odracza sięgnięcie po schema do czasu `.parse()`, gdy oba moduły są już
 * zewaluowane. Reużycie kontraktu filtra bez duplikacji.
 */
const filterRef: z.ZodType<z.infer<typeof filterConfigSchema>> = z.lazy(() => filterConfigSchema);

/**
 * Leniwa referencja do `widgetStyleSchema` z `list-view.ts` (ten sam cykl
 * importów co `filterRef`). Pozwala karcie widgetu mieć WŁASNY wygląd
 * nadpisujący domyślny styl pulpitu (`pageConfig.widgetStyle`). Runtime
 * paska stosuje `widget.style ?? widgetStyle` (per-widget > domyślny).
 */
const styleRef: z.ZodType<z.infer<typeof widgetStyleSchema>> = z.lazy(() => widgetStyleSchema);

// ── Typ widgetu ──────────────────────────────────────────────────────────────

/**
 * 14 typów widgetów (parytet SellCall). `counter` = liczba rekordów wg filtra;
 * `aggregate` = agregacja (suma/średnia/…) na polu. Pozostałe to wizualizacje
 * dochodzące w kolejnych iteracjach.
 */
export const widgetTypeSchema = z.enum([
  'counter',
  'aggregate',
  'progress',
  'pie',
  'bar',
  'donut',
  'trend',
  'sparkline',
  'gauge',
  'kpi',
  'table',
  'heatmap',
  'funnel',
  'stat_card',
]);

export type WidgetType = z.infer<typeof widgetTypeSchema>;

// ── Funkcje agregujące ───────────────────────────────────────────────────────

/**
 * Funkcja agregująca dla widgetów liczbowych/wykresów. I1 wspiera SQL-owe
 * `count`/`sum`/`avg`/`min`/`max`/`count_distinct`; `median`/`stddev`/`variance`
 * w schema dla parytetu (backend dolicza w kolejnych iteracjach).
 */
export const aggregateFunctionSchema = z.enum([
  'count',
  'sum',
  'avg',
  'min',
  'max',
  'median',
  'stddev',
  'variance',
  'count_distinct',
]);

export type AggregateFunction = z.infer<typeof aggregateFunctionSchema>;

// ── Presety zakresu dat + okresy porównawcze (trend/kpi) ─────────────────────

export const dateRangePresetSchema = z.enum([
  'today',
  'yesterday',
  'last_7_days',
  'last_14_days',
  'last_30_days',
  'last_60_days',
  'last_90_days',
  'last_180_days',
  'last_365_days',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'this_year',
  'last_year',
  'all_time',
]);

export type DateRangePreset = z.infer<typeof dateRangePresetSchema>;

export const comparisonPeriodSchema = z.enum([
  'previous_period',
  'same_period_last_year',
  'same_period_last_month',
  'same_period_last_week',
  'custom',
]);

export type ComparisonPeriod = z.infer<typeof comparisonPeriodSchema>;

// ── Format wartości ──────────────────────────────────────────────────────────

export const valueFormatTypeSchema = z.enum([
  'number',
  'currency',
  'percent',
  'duration',
  'bytes',
  'compact',
  'custom',
]);

export type ValueFormatType = z.infer<typeof valueFormatTypeSchema>;

export const animationTypeSchema = z.enum(['none', 'fade', 'slide', 'count_up', 'pulse']);

export type AnimationType = z.infer<typeof animationTypeSchema>;

/**
 * Format prezentacji wartości liczbowej w karcie/wykresie (prefix/suffix,
 * liczba miejsc po przecinku, separator tysięcy, waluta).
 */
export const widgetValueFormatSchema = z.object({
  type: valueFormatTypeSchema.optional(),
  prefix: z.string().max(20).optional(),
  suffix: z.string().max(20).optional(),
  decimals: z.number().int().min(0).max(10).optional(),
  thousandsSeparator: z.boolean().optional(),
  compactDisplay: z.boolean().optional(),
  currency: z.string().max(10).optional(),
  locale: z.string().max(20).optional(),
});

export type WidgetValueFormat = z.infer<typeof widgetValueFormatSchema>;

// ── Formatowanie warunkowe wartości widgetu ──────────────────────────────────

/**
 * Reguła formatowania warunkowego wartości widgetu (kolor/ikona gdy wartość
 * spełnia warunek). Np. „> 1000 → czerwony". Doszlifowane w I4.
 */
export const widgetConditionalFormatSchema = z.object({
  condition: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between', 'not_between']),
  value: z.number(),
  value2: z.number().optional(),
  color: z.string().max(30),
  backgroundColor: z.string().max(30).optional(),
  icon: z.string().max(50).optional(),
  label: z.string().max(100).optional(),
  animation: animationTypeSchema.optional(),
  priority: z.number().int().optional(),
});

export type WidgetConditionalFormat = z.infer<typeof widgetConditionalFormatSchema>;

// ── Cel/próg (progress / target) ─────────────────────────────────────────────

export const widgetTargetSchema = z.object({
  value: z.number(),
  label: z.string().max(100).optional(),
  showProgress: z.boolean().optional(),
  warningThreshold: z.number().optional(),
  criticalThreshold: z.number().optional(),
});

export type WidgetTarget = z.infer<typeof widgetTargetSchema>;

// ── Porównanie okresów (kpi) ─────────────────────────────────────────────────

export const widgetComparisonSchema = z.object({
  enabled: z.boolean(),
  period: comparisonPeriodSchema,
  customDays: z.number().int().positive().optional(),
  showAbsolute: z.boolean().optional(),
  showPercent: z.boolean().optional(),
  /** Dla metryk gdzie spadek = dobrze (odwraca kolory zielony/czerwony). */
  invertColors: z.boolean().optional(),
});

export type WidgetComparison = z.infer<typeof widgetComparisonSchema>;

// ── Ustawienia trendu (trend) ────────────────────────────────────────────────

export const widgetTrendSettingsSchema = z.object({
  dateRange: dateRangePresetSchema,
  dateField: z.string().min(1),
  groupBy: z.enum(['hour', 'day', 'week', 'month', 'quarter', 'year']),
  showChange: z.boolean().optional(),
  comparedTo: comparisonPeriodSchema.optional(),
  showMovingAverage: z.boolean().optional(),
  movingAveragePeriod: z.number().int().positive().optional(),
  showGoalLine: z.boolean().optional(),
  goalValue: z.number().optional(),
  fillArea: z.boolean().optional(),
  smoothed: z.boolean().optional(),
});

export type WidgetTrendSettings = z.infer<typeof widgetTrendSettingsSchema>;

// ── Ustawienia wykresu pie/bar/donut (chart) ─────────────────────────────────

export const widgetChartSettingsSchema = z.object({
  groupByField: z.string().min(1),
  showLegend: z.boolean().optional(),
  legendPosition: z.enum(['top', 'bottom', 'left', 'right']).optional(),
  showLabels: z.boolean().optional(),
  showValues: z.boolean().optional(),
  showPercent: z.boolean().optional(),
  maxItems: z.number().int().positive().optional(),
  sortBy: z.enum(['value', 'label']).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  colorScheme: z.enum(['default', 'pastel', 'vibrant', 'monochrome', 'custom']).optional(),
  customColors: z.array(z.string().max(30)).optional(),
  /** Dla wykresów słupkowych — orientacja pozioma. */
  horizontal: z.boolean().optional(),
  stacked: z.boolean().optional(),
  showGrid: z.boolean().optional(),
});

export type WidgetChartSettings = z.infer<typeof widgetChartSettingsSchema>;

// ── Ustawienia gauge ─────────────────────────────────────────────────────────

export const widgetGaugeSettingsSchema = z.object({
  minValue: z.number(),
  maxValue: z.number(),
  thresholds: z
    .array(
      z.object({
        value: z.number(),
        color: z.string().max(30),
        label: z.string().max(100).optional(),
      }),
    )
    .optional(),
  showNeedle: z.boolean().optional(),
  arcWidth: z.number().positive().optional(),
  showTicks: z.boolean().optional(),
  tickCount: z.number().int().positive().optional(),
});

export type WidgetGaugeSettings = z.infer<typeof widgetGaugeSettingsSchema>;

// ── Ustawienia mini-tabeli (table) ───────────────────────────────────────────

export const widgetTableSettingsSchema = z.object({
  columns: z.array(
    z.object({
      field: z.string().min(1),
      label: z.string().max(100).optional(),
      width: z.number().int().positive().optional(),
      align: z.enum(['left', 'center', 'right']).optional(),
      format: valueFormatTypeSchema.optional(),
    }),
  ),
  maxRows: z.number().int().positive(),
  sortField: z.string().optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  showHeader: z.boolean().optional(),
  alternateRows: z.boolean().optional(),
  highlightFirst: z.boolean().optional(),
});

export type WidgetTableSettings = z.infer<typeof widgetTableSettingsSchema>;

// ── Ustawienia funnel ────────────────────────────────────────────────────────

export const widgetFunnelSettingsSchema = z.object({
  stages: z.array(
    z.object({
      field: z.string().min(1),
      value: z.unknown(),
      label: z.string().max(100),
      color: z.string().max(30).optional(),
    }),
  ),
  showConversion: z.boolean().optional(),
  showDropoff: z.boolean().optional(),
  orientation: z.enum(['vertical', 'horizontal']).optional(),
});

export type WidgetFunnelSettings = z.infer<typeof widgetFunnelSettingsSchema>;

// ── Ustawienia heatmap ───────────────────────────────────────────────────────

export const widgetHeatmapSettingsSchema = z.object({
  dateField: z.string().min(1),
  valueField: z.string().optional(),
  colorScale: z.enum(['green', 'blue', 'red', 'purple', 'orange', 'custom']),
  customColors: z.array(z.string().max(30)).optional(),
  showWeekdays: z.boolean().optional(),
  showMonths: z.boolean().optional(),
  cellSize: z.number().int().positive().optional(),
});

export type WidgetHeatmapSettings = z.infer<typeof widgetHeatmapSettingsSchema>;

// ── Ustawienia sparkline ─────────────────────────────────────────────────────

export const widgetSparklineSettingsSchema = z.object({
  height: z.number().int().positive().optional(),
  showArea: z.boolean().optional(),
  showDots: z.boolean().optional(),
  smoothed: z.boolean().optional(),
  fillOpacity: z.number().min(0).max(1).optional(),
  strokeWidth: z.number().positive().optional(),
});

export type WidgetSparklineSettings = z.infer<typeof widgetSparklineSettingsSchema>;

// ── Metryka stat_card (wiele metryk w jednej karcie) ─────────────────────────

export const statCardMetricSchema = z.object({
  id: z.string().min(1),
  label: z.string().max(100),
  field: z.string().optional(),
  aggregateFunction: aggregateFunctionSchema.optional(),
  filters: z.array(filterRef).optional(),
  format: valueFormatTypeSchema.optional(),
  size: z.enum(['small', 'medium', 'large']).optional(),
});

export type StatCardMetric = z.infer<typeof statCardMetricSchema>;

// ── Akcja kliknięcia ─────────────────────────────────────────────────────────

/**
 * Co dzieje się po kliknięciu widgetu. `filter` = zawęża listę do warunków
 * widgetu (główny use-case I1), `none` = nieklikalne, reszta dochodzi później.
 */
export const widgetClickActionSchema = z.enum([
  'filter',
  'none',
  'link',
  'drill_down',
  'modal',
  'export',
]);

export type WidgetClickAction = z.infer<typeof widgetClickActionSchema>;

// ── Pełna konfiguracja widgetu ───────────────────────────────────────────────

/**
 * Jeden widget na pulpicie nad listą. Kształt portowany 1:1 z SellCall — większość
 * pól opcjonalna, `type` decyduje które ustawienia są używane przy renderze.
 * Płaski obiekt (nie dyskryminowana unia) — zgodny z SellCall i odporny na
 * dokładanie typów bez breaking change.
 */
export const widgetConfigSchema = z.object({
  /** UUID widgetu — generowany po stronie klienta. */
  id: z.string().min(1),
  /** Etykieta nad wartością (np. „Aktywni kontrahenci"). */
  label: z.string().min(1).max(120),
  /** Opcjonalny opis/podtytuł. */
  description: z.string().max(300).optional(),
  /** Kolor akcentu karty (hex lub token). */
  color: z.string().max(30),
  /** Kolor pomocniczy (gradienty / drugi szereg). */
  secondaryColor: z.string().max(30).optional(),
  /** Nazwa ikony (lucide). */
  icon: z.string().max(50).optional(),

  /** Typ widgetu — domyślnie `counter`. */
  type: widgetTypeSchema.optional().default('counter'),
  /** Szerokość w jednostkach siatki (1–4). */
  size: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
  height: z.enum(['auto', 'small', 'medium', 'large', 'xlarge']).optional(),

  /**
   * Własny wygląd tej karty (tło/gradient/szkło/kolory/zaokrąglenie/cień).
   * Brak = karta dziedziczy domyślny styl pulpitu (`pageConfig.widgetStyle`).
   * Edytowane w modalu widgetu (zakładka „Widgety" Kreatora Listy).
   */
  style: styleRef.optional(),

  /** Filtrowanie — pojedynczy warunek lub lista warunków + logika AND/OR. */
  filter: filterRef.optional(),
  filters: z.array(filterRef).optional(),
  filterLogic: z.enum(['and', 'or']).optional(),

  /** Agregacja (dla `aggregate`/wykresów) — funkcja + pole. */
  aggregateFunction: aggregateFunctionSchema.optional(),
  aggregateField: z.string().optional(),

  /** Cel/próg (dla `progress`). */
  target: widgetTargetSchema.optional(),

  /** Format prezentacji wartości. */
  format: widgetValueFormatSchema.optional(),

  /** Formatowanie warunkowe wartości. */
  conditionalFormats: z.array(widgetConditionalFormatSchema).optional(),

  /** Porównanie okresów (dla `kpi`). */
  comparison: widgetComparisonSchema.optional(),

  /** Ustawienia per typ. */
  trendSettings: widgetTrendSettingsSchema.optional(),
  chartSettings: widgetChartSettingsSchema.optional(),
  gaugeSettings: widgetGaugeSettingsSchema.optional(),
  tableSettings: widgetTableSettingsSchema.optional(),
  funnelSettings: widgetFunnelSettingsSchema.optional(),
  heatmapSettings: widgetHeatmapSettingsSchema.optional(),
  sparklineSettings: widgetSparklineSettingsSchema.optional(),
  statCardMetrics: z.array(statCardMetricSchema).optional(),

  /** Akcja po kliknięciu — domyślnie `filter` (zawęża listę). */
  clickAction: widgetClickActionSchema.optional().default('filter'),
  linkUrl: z.string().optional(),
  modalTitle: z.string().max(120).optional(),

  /** Auto-odświeżanie wartości. */
  autoRefresh: z.boolean().optional(),
  refreshInterval: z.number().int().positive().optional(),

  /** Kolejność wyświetlania (0-indexed). */
  order: z.number().int().nonnegative(),
});

export type WidgetConfig = z.infer<typeof widgetConfigSchema>;

/**
 * Lista widgetów pulpitu — wpinana do `ListConfig.widgets`.
 */
export const widgetsConfigSchema = z.array(widgetConfigSchema);

export type WidgetsConfig = z.infer<typeof widgetsConfigSchema>;
